import InstructorSubmissionChatPanel from 'components/Chat/InstructorSubmissionChatPanel';
import FeedbackRegenerating from 'components/Grading/FeedbackRegenerating';
import FileViewer from 'components/Grading/FileViewer';
import Footer from 'components/InstructorViewFormativeSubmission/Footer';
import Header from 'components/InstructorViewFormativeSubmission/Header';
import FeedbackRenderer from 'components/StudentViewSubmission/FeedbackRenderer';
import { useApp } from 'contexts/app';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    ButtonGroup,
    Container,
    IconButton,
    PDFViewer,
    SheetViewer,
    Stack,
    styled,
    VideoViewer,
} from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Mobile = ({
    tab,
    setTab,
    selectedFeedbackId,
    setSelectedFeedbackId,
    selectedInlineCommentId,
    setSelectedInlineCommentId,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    isGenerating,
    currentSubmission,
    highlights,
    onDownloadChatCsv,
}) => {
    const { currentAssignment } = useApp();
    const { t } = useTranslation('assignment');

    const currentFile = currentSubmission.files[0];
    const url = useMemo(() => getS3FileURL(currentSubmission.files[0]), [currentSubmission]);
    const renderableHighlights = highlights.map(({ id, value }) => {
        if (typeof value === 'string') return { id, value };
        return { id: id, index: value.index, value: value.highlight };
    });

    return (
        <CustomContainer>
            <Header />
            <CustomStack>
                <TopStack isDocumentFullScreen={isDocumentFullScreen}>
                    {currentFile && (
                        <Box sx={{ height: '100%' }}>
                            {currentFile.type === 'application/pdf' ? (
                                <PDFViewer
                                    url={url}
                                    highlights={renderableHighlights}
                                    inlineComments={currentSubmission.ai.inline_comments}
                                    setInlineComments={() => {}}
                                    selectedInlineCommentId={selectedInlineCommentId}
                                    setSelectedInlineCommentId={setSelectedInlineCommentId}
                                    isFullScreen={isDocumentFullScreen}
                                    setIsFullScreen={setIsDocumentFullScreen}
                                    isStudent
                                />
                            ) : currentFile.type === 'text/csv' ? (
                                <SheetViewer highlights={renderableHighlights} url={url} />
                            ) : currentFile.type.startsWith('video/') ||
                              currentFile.type.startsWith('application/mp4') ? (
                                <VideoViewer
                                    highlights={renderableHighlights}
                                    mimeType={currentFile.type}
                                    transcript={currentFile.transcript}
                                    url={url}
                                />
                            ) : (
                                <FileViewer docUrl={url} fileType={currentFile.type} />
                            )}
                        </Box>
                    )}
                </TopStack>
                <BottomStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isGenerating ? (
                        <FeedbackRegenerating sx={{ top: '40%' }} />
                    ) : (
                        <>
                            <Stack sx={{ px: 3, py: 2 }} direction={'row'} justifyContent={'space-between'}>
                                <ButtonGroup
                                    options={[
                                        {
                                            key: 'feedback',
                                            value: t('feedback-section.feedback-label', 'Feedback'),
                                        },
                                        {
                                            key: 'inquiry',
                                            value: t('chat.inquiry-label', 'Inquiry'),
                                        },
                                    ]}
                                    selected={tab}
                                    onChange={(tab) => setTab(tab)}
                                />
                                {tab === 'inquiry' && (
                                    <IconButton
                                        sx={{ alignSelf: 'flex-end' }}
                                        aria-label={'download'}
                                        kind={'download'}
                                        size={'medium'}
                                        variant={'square'}
                                        tooltip={{
                                            component: t('chat.download-tooltip', 'Download the full chat history'),
                                        }}
                                        onClick={onDownloadChatCsv}
                                    />
                                )}
                            </Stack>
                            {tab === 'feedback' && (
                                <FeedbackRenderer
                                    assignment={currentAssignment}
                                    currentSubmission={currentSubmission}
                                    selectedFeedbackId={selectedFeedbackId}
                                    setSelectedFeedbackId={setSelectedFeedbackId}
                                />
                            )}
                            {tab === 'inquiry' && (
                                <InstructorSubmissionChatPanel currentSubmission={currentSubmission} isOpen={true} />
                            )}
                        </>
                    )}
                </BottomStack>
            </CustomStack>
            <Footer />
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        background: theme.palette.ensightful.neutral[100],
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        position: 'absolute',
        top: '113px',
        width: '100%',
        height: 'calc(100vh - 113px - 61px)',
    };
});

const TopStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? '100%' : '50%',
        background: theme.palette.ensightful.shades[0],
        transition: 'height 150ms',
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        height: isDocumentFullScreen ? 0 : '50%',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'height 150ms',
        ...(!isDocumentFullScreen && {
            borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        }),
    };
});

export default Mobile;
